import { Component, Input, OnChanges, OnInit, signal, WritableSignal, SimpleChanges, effect, } from '@angular/core';
import { Constants } from 'src/app/shared/utils/constants';
import { EEAGapSurfaceWaterColdef } from './eea-gap-surfacewater.coldef';
import { EEAGapApiService } from '../eea-gap.api.service';
import { EEAGapLogicService } from '../eea-gap.logic.service';
import { EEAGAPMenuLogicService } from '../eea-gap/eea-gap-menu.logic.service';
import { ApplicationDateSelection, ApplicationDateSurfaceWater } from 'src/app/shared/models/application-date';
import { ApplicationDateApiService } from 'src/app/shared/services/application-date.api.service';
import { CropList } from 'src/app/shared/models/crop-list';
import { EeaGapNonCoreCompartmentComponent } from '../eea-gap-non-core-compartment/eea-gap-non-core-compartment.component';
import { CropListMatching } from 'src/app/shared/models/crop-list-matching';
import { catchError, firstValueFrom, forkJoin } from 'rxjs';
import { ApplicationSchemeXApplicationWindow } from 'src/app/shared/models/application-scheme';
import { Utils } from 'src/app/shared/utils/utils';
import { RowNode } from 'ag-grid-community';
import { DatePipe } from '@angular/common';
import { GapApplicationSchemeLogicService } from 'src/app/shared/services/gap-application-scheme.logic.service';
import { ExcelWriterService } from 'src/app/shared/services/excel-writer.service';
import { MitigationsXApplicationScheme } from 'src/app/shared/models/mitigation-x-application-scheme';
import { EndpointGet } from 'src/app/shared/models/endpoint';
import { ApplicationSchemeGap } from '../../eea-output/eea-output-project/eea-output-interfaces/eea-out-put-gap-interface';
import Swal from 'sweetalert2';
import { Swash63ApplicationWindow } from '../eea-gap-swash63.logic.service';

@Component({
  selector: 'app-eea-gap-surfacewater',
  templateUrl: '../eea-gap-non-core-compartment/eea-gap-non-core-compartment.component.html',
  styleUrls: ['./eea-gap-surfacewater.component.css']
})
export class EEAGapSurfaceWaterComponent extends EeaGapNonCoreCompartmentComponent implements OnInit, OnChanges {

  @Input() override isProjectOwnershipValid: boolean = false;
  visible: boolean = false;
  cropInterceptionsStep1And2: any[] = [];
  override cropListMatchings: CropListMatching[] = [];
  applicationDates: ApplicationDateSurfaceWater[] = [];
  override applicationDatesByCrop: ApplicationDateSurfaceWater[] = [];
  override compartment: string = Constants.COMPARTMENTS.SURFACE_WATER;
  private readonly swash63DateRequests = new WeakMap<object, object>();

  private get hasSwash63(): boolean {
    return this.swash63GapLogicService.hasSwash63(this.selectedProject);
  }

  private get useSwash63Dates(): boolean {
    return !this.isInverseModeling && this.hasSwash63;
  }

  constructor(
    private gapSWApiService: EEAGapApiService,
    private gapSurfaceWaterColdef: EEAGapSurfaceWaterColdef,
    private gapLogicSWService: EEAGapLogicService,
    EEAGAPMenuLogicService: EEAGAPMenuLogicService,
    private applicationDateSWApiService: ApplicationDateApiService,
    datepipe: DatePipe,
    gapApplicationSchemeLogicService: GapApplicationSchemeLogicService,
    gapExcelWriterService: ExcelWriterService,) {
    super(gapSWApiService, gapLogicSWService, EEAGAPMenuLogicService, applicationDateSWApiService, datepipe, gapApplicationSchemeLogicService, gapExcelWriterService);

  }



  override fillInitValues(changes: SimpleChanges): void {
    let promises = [
      this.gapLogicSWService.getCompartments(),
      this.gapLogicSWService.getRegionCountry(changes['selectedProject'].currentValue),
      this.gapLogicSWService.getAllCropList(),
      this.gapLogicSWService.getStep1n2Interceptions(),
      this.gapLogicSWService.getCropListMatchings(),
      this.gapLogicSWService.getCropInterceptions(),
      this.gapLogicSWService.getCropList('Europe Union', 'Surface Water', 'Spray'),
      this.gapLogicSWService.getSaturatedVaporPressureByProjectPk(this.selectedProject?.projectPk),
    ]
    Promise.all(promises)
      .then((results) => {
        this.compartmentList = results[0];
        this.geographiesList = results[1];
        this.cropList = results[2];
        this.cropInterceptionsStep1And2 = results[3];
        this.cropListMatchings = results[4];
        this.cropListEVA = results[6];
        this.vaporPressureValues = results[7] as EndpointGet[];
        this.getApplicationSchemesByProjectAndCompartment(changes['selectedProject'].currentValue.projectPk, this.getCoreCompartment());
      })
  }

  override getColDefFilteredBySelectedModels(applicationSchemes?: ApplicationSchemeGap[]) {
    if (!this.selectedProject) return;
    let models = this.gapLogicSWService.getModelsByCompartment(this.compartment, this.selectedProject);
    let columnsByModel = this.gapLogicSWService.getDisplayedColumnsByModel(models, this.selectedProject.geography!);
    const useDeposition = this.getApplicationWindowSelectionUseDeposition(applicationSchemes);
    this.gapLogicSWService.useDeposition.set(useDeposition);
    const colDefParams = {
      bbch: this.gapLogicSWService.bbch,
      numerOfApplications: this.swash63GapLogicService.getNumberOfApplicationsOptions(this.selectedProject, this.gapLogicSWService.numerOfApplications),
      applicationMethods: Object.keys(Constants.APPLICATION_METHOD_VALUES_SURFACEWATER).map(key => ({ value: key })),
      geographiesList: this.geographiesList,
      cropList: Utils.sortObject((this.selectedProject.geography == Constants.CROP_GEOGRAPHIES.USA) ? this.cropList?.filter((x: CropList) => x.Geography === Constants.CROP_GEOGRAPHIES.USA) : this.cropList, 'CropName'),
      geographySelected: this.selectedProject.geography,
      cropListMatching: this.cropListMatchings,
      selectedModels: models,
      useDeposition,
    };

    const columnsDefinition = this.isInverseModeling
      ? this.gapSurfaceWaterColdef.getInverseColumnsDefinition(colDefParams, this.selectedProject, this.isProjectOwnershipValid)
      : this.gapSurfaceWaterColdef.getColumnsDefinition(colDefParams, this.isProjectOwnershipValid, this.cropListEVA);
    this.detailsColumsDef = this.gapSurfaceWaterColdef.configureDetailGrid(colDefParams, this.isProjectOwnershipValid);

    let columnsToShow = [...Constants.GAP_CORE_FIELDS, ...new Set(columnsByModel), ...this.fixedColumns];
    if (this.isInverseModeling) columnsToShow = [...columnsToShow, ...Constants.GAP_CORE_INVERSE_MODELING_FIELDS];
    return this.gapLogicSWService.getFilteredColumns(columnsToShow, columnsDefinition);
  }

  override onClickViewButton(event: any) {
    if (event.field === Constants.FIELD_NAMES.STEP4_MITIGATION)
      this.showMitigationXApplicationSchemeDialog(event);
    else
      this.viewApplicationWindow(event);
  }

  override removeVariableRatesColumns(models: string[], columnsToShow: string[]): string[] {
    const columns = super.removeVariableRatesColumns(models, columnsToShow);
    return this.swash63GapLogicService.shouldHideVariableRatesColumn(this.isInverseModeling, models)
      ? columns.filter(column => column !== Constants.GAP_FIELD_NAMES.HAS_VARIABLE_RATES)
      : columns;
  }

  showMitigationXApplicationSchemeDialog(event: any) {
    this.mitigationXApplicationScheme.showMitigationxApplicationSchemeDialog(event.rowNode);
  }

  viewApplicationWindow(event: any) {
    this.selectedApplicationWindowRow = event.rowNode;
    this.applicationScheme = event.rowNode.data;
    if (this.useSwash63Dates && !event.rowNode.data.isChild) {
      this.loadSwash63ApplicationDates(event.rowNode, false, false, true);
      return;
    }

    let gapCropName = this.cropList?.find((x: CropList) => x.CropListPk === this.selectedApplicationWindowRow?.data?.cropAppDatePk)?.CropName!;
    let bbchEarliest = this.selectedApplicationWindowRow?.data?.earliest_bbch ? this.selectedApplicationWindowRow?.data?.earliest_bbch : this.selectedApplicationWindowRow?.data?.bbchEarliest;
    if (this.selectedApplicationWindowRow?.data?.cropAppDatePk && bbchEarliest) {
      let promises = [
        this.getSurfaceWaterApplicationDates(this.selectedApplicationWindowRow?.data?.cropAppDatePk, Number(bbchEarliest)),
      ]
      Promise.all(promises)
        .then((results) => {
          this.applicationDates = results[0];
          if (gapCropName && Constants.GAP_CROPS_WITHOUT_APP_DATES.includes(gapCropName ?? '')) {
            this.applicationDatesByCrop = this.applicationDates.filter((x: any) => Constants.GAP_CROP_SCENARIOS_WITHOUT_APP_DATES[gapCropName].includes(x.location ?? ''));
          }
          else {
            this.applicationDatesByCrop = this.applicationDates.filter((x: ApplicationDateSurfaceWater) => x.beginWindow != null || x.endWindow != null);
          }

          this.selectedApplicationWindowRow!.data.cropWithoutAppDates = false;
          this.getScenariosWithoutCropListPk();
          this.applicationDatesByCrop?.forEach((appDate: any) => {
            let value = event?.data?.applicationSchemeXApplicationWindow?.find((x: any) => x.applicationDateSurfaceWaterPk === appDate.applicationDateSurfaceWaterPk);
            if (value) {
              appDate.beginWindow = value.firstDate;
              appDate.endWindow = this.getEndDate(value.firstDate, this.selectedApplicationWindowRow!);
              appDate.min = value.min;
              appDate.max = value.max;
              appDate.increment = value.increment;
              appDate.applicationSchemeXApplicationWindowPk = value.applicationSchemeXApplicationWindowPk;
              appDate.runOff = value.runOff;
              appDate.sprayDrift = value.sprayDrift;
              appDate.deposition = value.deposition;
              appDate.bbchStart = value.bbchStart;
              appDate.bbchEnd = value.bbchEnd;
            }
            else if (gapCropName && Constants.GAP_CROPS_WITHOUT_APP_DATES.includes(gapCropName ?? '')) {
              appDate.beginWindow = this.datepipe.transform(this.gapLogicSWService.getNonLeapDate(), Constants.SERVICE_DATE_FORMAT)!;
              appDate.endWindow = this.datepipe.transform(this.gapLogicSWService.getNonLeapDate(), Constants.SERVICE_DATE_FORMAT)!;
            }
          });
          this.applicationDatesByCrop = this.applicationDatesByCrop.sort((a, b) => a.location.localeCompare(b.location));
          this.applicationDialog.showDialog(event.rowNode, this.compartment);
        });
    } else {
      this.applicationDialog.showDialog(event.rowNode, this.compartment);
    }
  }

  getApplicationWindowSelectionUseDeposition(applicationSchemes?: ApplicationSchemeGap[]) {
    const data = applicationSchemes ?? this.rowData;
    if (!data || data.length === 0) return false;
    return data.some((row: ApplicationSchemeGap) =>
        row.applicationSchemeXApplicationWindow?.some((appWindow: ApplicationSchemeXApplicationWindow) => appWindow.deposition === true)
      );
  }

  public getEndDate(firstDate: string, row: RowNode): string {
    return row?.data.isChild ?
      Utils.GetEndDateByApplication(firstDate, row?.data.min_application_interval, 1) :
      Utils.GetEndDateByApplication(firstDate, row?.data.applicationInterval, row?.data.numberOfApplications)
  }

  override getScenariosWithoutCropListPk() {
    let gapCrop = this.selectedApplicationWindowRow?.data?.cropAppDatePk;
    if (gapCrop) {
      let gapCropName = this.cropList?.find((x: CropList) => x.CropListPk === gapCrop)?.CropName;
      if (gapCropName && Constants.GAP_CROPS_WITHOUT_APP_DATES.includes(gapCropName ?? '')) {
        let scenarios = this.applicationDates.filter((x: ApplicationDateSurfaceWater) => x.cropListPk == gapCrop);
        this.selectedApplicationWindowRow!.data.cropWithoutAppDates = Constants.GAP_CROPS_WITHOUT_APP_DATES.includes(gapCropName ?? '');
        this.applicationDatesByCrop = scenarios;
      }
    }
  }

  override onSaveMitigationXApplicationScheme(params: any): void {
    let parentRowNode = this.grid.gridApi.getRowNode(params.parentRowNode.id);
    let oldvalue = params.parentRowNode?.data.mitigationsXApplicationScheme;
    let mitigationsXApplicationScheme = params.mitigationsXApplicationScheme.map((mitigation: any) => {
      let mitigationsXApplicationScheme: MitigationsXApplicationScheme = {
        mitigationName: mitigation.mitigationName,
        bufferZone: mitigation.bufferZone,
        fractionalReductionRunoffVolume: mitigation.fractionalReductionRunoffVolume,
        fractionalReductionRunoffFlux: mitigation.fractionalReductionRunoffFlux,
        fractionalReductionErosionMass: mitigation.fractionalReductionErosionMass,
        fractionalReductionErosionFlux: mitigation.fractionalReductionErosionFlux,
        nozzleReduction: mitigation.nozzleReduction,
        sprayDriftOption: mitigation.sprayDriftOption,
        sprayDriftBuffer: mitigation.sprayDriftBuffer,
        pondMassLoading: mitigation.pondMassLoading,
        ditchMassLoading: mitigation.ditchMassLoading,
        streamMassLoading: mitigation.streamMassLoading,
        distance: mitigation.distance
      }
      return mitigationsXApplicationScheme;
    });
    parentRowNode.data.mitigationsXApplicationScheme = mitigationsXApplicationScheme;
    this.grid.CreateTransaction(parentRowNode.id, parentRowNode.id, oldvalue, parentRowNode.data);
  }

  override onSaveApplicationDates(selectedAppDates: ApplicationDateSelection): void {
    let currentDates = selectedAppDates.parentRowNode.data.applicationSchemeXApplicationWindow;
    if (currentDates?.length !== selectedAppDates.selectedRows?.length) {
      this.createTransactionForApplicationDates(selectedAppDates);
    } else {
      for (const selectedDate of selectedAppDates.selectedRows) {
        const existAppDate = currentDates.some((currentDate: any) =>
          currentDate.applicationDateSurfaceWaterPk === selectedDate.data.applicationDateSurfaceWaterPk &&
          currentDate.firstDate === selectedDate.data.beginWindow &&
          currentDate.endDate === selectedDate.data.endWindow &&
          currentDate.min === selectedDate.data.min &&
          currentDate.max === selectedDate.data.max &&
          currentDate.increment === selectedDate.data.increment &&
          currentDate.runOff === selectedDate.data.runOff &&
          currentDate.sprayDrift === selectedDate.data.sprayDrift &&
          currentDate.deposition === selectedDate.data.deposition &&
          currentDate.bbchStart === selectedDate.data.bbchStart &&
          currentDate.bbchEnd === selectedDate.data.bbchEnd
        );
        if (!existAppDate) {
          this.createTransactionForApplicationDates(selectedAppDates);
          break;
        }
      }
    }

    this.grid.gridApi.forEachNode((node: any) => {
      if (node.data) {
        const hasDeposition = node.data.applicationSchemeXApplicationWindow?.some((appWindow: any) => appWindow.deposition === true) ?? false;
        node.data.deposition = hasDeposition;


        const depositionColumns = this.grid.gridColumnApi.getAllColumns()
          .filter((col: any) => col.getColId().startsWith(Constants.COLUMN_NAMES.DEPOSITION_RATE_MOLECULE_NAME))
          .map((col: any) => col.getColId());

        if (node) {
          this.grid.gridApi.refreshCells({
            force: true,
            columns: depositionColumns,
            rowNodes: [node]
          });
          this.grid.gridApi.redrawRows({ rowNodes: [node] });

        }

        this.grid.gridApi.refreshCells({ force: true });
      }
    });

    const anyDepositionSelected = this.rowData.flatMap((row: any) => row.applicationSchemeXApplicationWindow).some((applicationScheme: any) => applicationScheme.deposition);
    const projectModels = (this.selectedProject?.projectXCompoundXModel?.map(model => model?.ModelName) ?? []) as string[];
    const swanSelected = projectModels.includes(Constants.MODELS.SWAN);
    this.grid?.gridColumnApi.setColumnVisible(Constants.GAP_FIELD_NAMES.SWAN_CROP_INTERCEPTION, anyDepositionSelected);
    this.grid?.gridColumnApi.setColumnVisible(Constants.GAP_FIELD_NAMES.USE_SWAN_CROP_INTERCEPTION, anyDepositionSelected && swanSelected);
    const depositionColumns = this.grid?.gridColumnApi.getAllColumns()
      ?.filter((col: any) => col.getColId().startsWith(Constants.COLUMN_NAMES.DEPOSITION_RATE_MOLECULE_NAME));
    depositionColumns?.forEach((col: any) => {
      this.grid?.gridColumnApi.setColumnVisible(col.getColId(), anyDepositionSelected);
    });
    this.grid.gridApi.refreshCells( { force: true } );
    this.grid.gridApi.redrawRows();
  }

  override getApplicationSchemesByProjectAndCompartment(projectPk: number, compartmentPk: number) {
    let updateRequired = false;
    if (projectPk != 0) {
      this.loading = true;
      forkJoin({
        applicationSchemes: this.gapSWApiService.getApplicationSchemesByProjectAndCompartment(projectPk, compartmentPk),
      }).pipe(
        catchError((error) => {
          console.error(error);
          this.loading = false;
          return [];
        })
      ).subscribe({
        next: ({ applicationSchemes }) => {
          if (!this.selectedProject) return;

          this.gapLogicSWService.addActiveIngredientsRatesToGrid(applicationSchemes, this.getColDefFilteredBySelectedModels(applicationSchemes), this.isInverseModeling, this.selectedProject, this.isProjectOwnershipValid, this.compartment).then(() => {
            this.gapLogicSWService.addActiveIngredientsRatesToDetailGrid(this.gapLogicSWService.rowData, this.detailsColumsDef, this.isInverseModeling, this.selectedProject!, this.isProjectOwnershipValid, this.compartment);
            this.rowData = this.gapLogicSWService.rowData;
            this.rowData.forEach((row: any) => {
              let selectedModels = (this.selectedProject?.projectXCompoundXModel?.map(model => model?.ModelName) ?? []) as string[];
              let interceptionModel = Utils.getFirstMatchBetweenTwoArrays(Constants.MODELS_WITH_INTERCEPTIONS, selectedModels);
              row.selectedInterceptionModel = interceptionModel;
              row.useSwashCropInterception = row.useSwashCropInterception ?? false;
              row.useSwanCropInterception = row.useSwanCropInterception ?? false;
              if (this.hasSwash63) {
                this.swash63GapLogicService.setApplicationSchemeDefaults(row, this.selectedProject);
                this.updateSwash63ApplicationIntervals(row);
              }
              if (this.useSwash63Dates) {
                this.updateSwash63InterceptionCrop(row);
              }
              if (Utils.isEmptyValue(row.cropInterception))
                this.gapLogicSWService.setCropInterceptionByCropAndBBCH(row, this.compartment);
              if (this.hasSwash63) {
                this.updateSwash63ChildInterceptions(row);
              }
              this.gapLogicSWService.setCropCoverage(row);
            });
            this.columsDef = this.gapLogicSWService.columsDef;
            this.detailsColumsDef = this.gapLogicSWService.detailsColumnDef;
            this.refreshSelectedValuesTimeout();
            this.setLoadingState(false);
          });
        },
      });
    }
  }

  override onDropDownSelectionChanged(event: any) {
    event.row.geographies = event.row.applicationSchemeXGeography?.length > 0 ? this.geographiesList.filter((x: any) => event.row.applicationSchemeXGeography.map((y: any) => y.geographyPk).includes(x.key)) : [];
    if (event.field === Constants.GAP_FIELD_NAMES.IS_PHI_APPLICABLE) {
      this.grid.gridApi.redrawRows();
    }
    if (event.row?.isChild && event.field === 'application_method') {
      event.row.chemical_application_method = null;
      this.grid.detailGridApi?.refreshCells({ force: true });
    }
    if (this.hasSwash63 && !event.row?.isChild && event.field === Constants.GAP_FIELD_NAMES.APP_TIMING_DIRECTION) {
      this.updateSwash63ApplicationIntervals(event.row);
      this.grid.CreateTransaction(event.id, event.id, null, event.row);
      this.grid.detailGridApi?.redrawRows();
    }
    if (event.field === Constants.GAP_FIELD_NAMES.INTERCEPTION_PK || event.field === Constants.GAP_FIELD_NAMES.DRIFT_INTERCEPTION_PK || event.field === Constants.GAP_FIELD_NAMES.BBCH) {
      let selectedModels = (this.selectedProject?.projectXCompoundXModel?.map(model => model?.ModelName) ?? []) as string[];
      let interceptionModel = Utils.getFirstMatchBetweenTwoArrays(Constants.MODELS_WITH_INTERCEPTIONS, selectedModels);
      event.row.selectedInterceptionModel = interceptionModel;
      switch (event.field) {
        case Constants.GAP_FIELD_NAMES.DRIFT_INTERCEPTION_PK:
          event.row.cropDriftInterceptionPk = event.value;
          break;
        case Constants.GAP_FIELD_NAMES.INTERCEPTION_PK:
          event.row.cropInterceptionPk = event.value;
          break;
        default:
          break;
      }
      this.gapLogicSWService.setCropInterceptionByCropAndBBCH(event.row, this.compartment);
      if (this.useSwash63Dates) {
        this.refreshSwash63InterceptionCell(event.id);
      }
    }
    if (this.useSwash63Dates && !event.row?.isChild && event.field === Constants.GAP_FIELD_NAMES.CROP_APP_DATE_PK) {
      this.updateSwash63InterceptionCrop(event.row);
    }
    else if (event.field === Constants.GAP_FIELD_NAMES.APPLICATION_METHOD) {
      this.grid.gridApi.redrawRows();
    }
    if (this.hasSwash63 && !event.row?.isChild && [
      Constants.GAP_FIELD_NAMES.CROP_APP_DATE_PK,
      Constants.GAP_FIELD_NAMES.INTERCEPTION_PK,
      Constants.GAP_FIELD_NAMES.BBCH,
    ].includes(event.field)) {
      this.updateSwash63ChildInterceptions(event.row, true);
      this.grid.detailGridApi?.refreshCells({ columns: ['crop_interception'], force: true });
    }
    if (event.field === Constants.GAP_FIELD_NAMES.CROP_APP_DATE_PK || event.field === Constants.GAP_FIELD_NAMES.BBCH) {
      this.setCropPkToChildren(this.grid.gridApi.getRowNode(event.id))
      if (this.useSwash63Dates && !event.row.isChild) {
        this.loadSwash63ApplicationDates(this.grid.gridApi.getRowNode(event.id), true, true);
      } else {
        this.saveAppWindowsAfterLoad(this.grid.gridApi.getRowNode(event.id));
      }
    }
    if (this.useSwash63Dates && !event.row.isChild && event.field === Constants.GAP_FIELD_NAMES.BBCH_LATEST) {
      this.loadSwash63ApplicationDates(this.grid.gridApi.getRowNode(event.id), false, true);
    }
    if (event.field === Constants.GAP_FIELD_NAMES.APPLICATION_NUMBER) {
      if (event.value === 1) {
        event.row.applicationInterval = 1;
      }
      this.gapLogicSWService.setInnerGridValues(event, this.grid);
      if (this.hasSwash63) {
        this.updateSwash63ApplicationIntervals(event.row);
        this.updateSwash63ChildInterceptions(event.row);
      }
      this.grid.gridApi.redrawRows();
      if (this.useSwash63Dates && event.value === 1) {
        this.loadSwash63ApplicationDates(this.grid.gridApi.getRowNode(event.id));
      }
    }
    if (event.field === Constants.GAP_FIELD_NAMES.HAS_VARIABLE_RATES) {
      if (!event.value) {
        event.row.isExpanded = false;
        this.gapLogicSWService.toggleDetailGrid(event, this.grid);
      }
      this.grid.gridApi.redrawRows();
    }
    if (event.field === Constants.GAP_FIELD_NAMES.USE_SWASH_CROP_INTERCEPTION) {
      if (!event.value) {
        event.row.swashCropInterception = event.row.cropInterception;
      }
      this.grid.gridApi.redrawRows();
    }
    if (event.field === Constants.GAP_FIELD_NAMES.USE_SWAN_CROP_INTERCEPTION) {
      if (!event.value) {
        event.row.swanCropInterception = event.row.cropInterception;
      }
      this.grid.gridApi.redrawRows();
    }
    this.grid.gridApi.refreshCells();
  }

  override onCalendarSelectionChanged(event: any): void {
    if (!event.row?.isChild) {
      super.onCalendarSelectionChanged(event);
      return;
    }
    event.row[event.field] = this.datepipe.transform(event.value, Constants.SERVICE_DATE_FORMAT);
    this.grid.CreateTransaction(event.id, event.id, null, event.row);
  }

  override createTransactionForApplicationDates(selectedAppDates: ApplicationDateSelection): void {
    let parentRowNode = this.grid.gridApi.getRowNode(selectedAppDates?.parentRowNode?.data?.isChild ? selectedAppDates?.parentRowNode?.data?.parentRowNodeId : selectedAppDates?.parentRowNode?.id);
    let oldvalue = selectedAppDates?.parentRowNode?.data?.isChild ? selectedAppDates?.parentRowNode?.data?.applicationSchemeXApplicationWindow : parentRowNode?.data.applicationSchemeXApplicationWindow;
    let dates = selectedAppDates.selectedRows.map((x: any) => {
      let object: ApplicationSchemeXApplicationWindow = {
        applicationDateSurfaceWaterPk: x.data.applicationDateSurfaceWaterPk,
        firstDate: this.useSwash63Dates && !selectedAppDates.parentRowNode.data.isChild
          ? (x.data.beginWindow ? new Date(x.data.beginWindow) : undefined)
          : this.gapLogicSWService.transformLeapDate(x.data.beginWindow),
        endDate: this.useSwash63Dates && !selectedAppDates.parentRowNode.data.isChild
          ? (x.data.endWindow ? new Date(x.data.endWindow) : undefined)
          : this.gapLogicSWService.transformLeapDate(new Date(this.getEndDate(x.data.beginWindow, selectedAppDates?.parentRowNode))),
        min: x.data.min,
        max: x.data.max,
        increment: x.data.increment,
        application_scheme_x_application_pk: x.data.application_scheme_x_application_window_pk,
        runOff: x.data.runOff,
        sprayDrift: x.data.sprayDrift,
        deposition: x.data.deposition,
        bbchStart: x.data.bbchStart,
        bbchEnd: x.data.bbchEnd
      }
      if (this.useSwash63Dates && !selectedAppDates.parentRowNode.data.isChild) {
        object.location = x.data.location;
        object.season = x.data.season;
      }
      return object;
    })
    if (selectedAppDates?.parentRowNode?.data.isChild) {
      if (Utils.isEmptyValue(parentRowNode?.data?.applicationSchemeXApplication) || parentRowNode?.data?.applicationSchemeXApplication?.length === 0 || parentRowNode?.data?.applicationSchemeXApplication?.length !== parentRowNode.data?.children?.length)
        parentRowNode.data.applicationSchemeXApplication = parentRowNode.data?.children;
      parentRowNode.data.applicationSchemeXApplication[selectedAppDates?.parentRowNode?.data?.application_number - 1].applicationSchemeXApplicationWindow = dates;
    }
    else {
      parentRowNode.data.applicationSchemeXApplicationWindow = dates;
      if (this.useSwash63Dates) {
        if (!this.swash63GapLogicService.isPrzmInputEnabled(parentRowNode.data)) {
          parentRowNode.data.chemicalApplicationMethod = null;
          parentRowNode.data.soilDepth = null;
        }
        parentRowNode.data.applicationDate = this.swash63GapLogicService.getCommonApplicationDate(dates);
        this.swash63GapLogicService.updateApplicationWarnings(parentRowNode.data, selectedAppDates.selectedRows.map(row => row.data));
        this.grid.gridApi.refreshCells({ rowNodes: [parentRowNode], force: true });
      }
    }

    const hasDeposition = dates?.some((appWindow: any) => appWindow.deposition === true) ?? false;
    parentRowNode.data.deposition = hasDeposition;

    this.grid.CreateTransaction(parentRowNode.id, parentRowNode.id, oldvalue, parentRowNode.data);
  }

  override onFirstDataRendered(params: any): void {
    if (params?.type === Constants.GRID_EVENTS.FIRST_DATA_RENDERED) {
      this.applicationDatesByCrop = [];
      this.grid.gridApi.forEachNode((node: RowNode) => {
        node!.data.useSwashCropInterception = node!.data.useSwashCropInterception ?? false;
        node!.data.useSwanCropInterception = node!.data.useSwanCropInterception ?? false;
        if (node?.data?.coreApplicationSchemePk) {
          node!.data.reloadCore = this.validateCoreCropPK(node, this.compartment, this.selectedProject?.geography!, this.cropListMatchings);
        }
        if (this.useSwash63Dates || node?.data?.applicationSchemeXApplicationWindow?.length == 0) {
          this.saveAppWindowsAfterLoad(node);
        }
        if (node?.data?.numberOfApplications === 1) {
          node!.data.applicationInterval = 1;
          node!.data.hasVariableRates = false;
        }
      });
      this.grid.gridApi.redrawRows();
      this.grid.gridApi.forEachNode((node: RowNode) => { node!.data.reloadCore = false; });
    }
  }

  saveAppWindowsAfterLoad(node: RowNode): void {
    if (this.useSwash63Dates && !node.data.isChild) {
      this.loadSwash63ApplicationDates(node);
      return;
    }
    let gapCropPk: number | undefined = node.data?.cropAppDatePk;
    let gapCropName: string = "";

    if (Utils.isEmptyValue(gapCropPk)) {
      gapCropPk = this.getCropValueMatchings(node);
    }
    gapCropName = this.cropList?.find((x: CropList) => x.CropListPk === gapCropPk)?.CropName!;

    if (gapCropPk && node.data?.bbchEarliest) {
      let promises = [
        this.getSurfaceWaterApplicationDates(gapCropPk, Number(node.data?.bbchEarliest)),
      ]
      Promise.all(promises)
        .then((results) => {
          let appDates: any[] = [];
          this.applicationDates = results[0];

          if (gapCropName && Constants.GAP_CROPS_WITHOUT_APP_DATES.includes(gapCropName ?? '')) {
            this.applicationDatesByCrop = this.applicationDates.filter((x: any) => Constants.GAP_CROP_SCENARIOS_WITHOUT_APP_DATES[gapCropName].includes(x.location ?? ''));
            this.applicationDatesByCrop?.forEach((appDate: ApplicationDateSurfaceWater) => {
              appDate.beginWindow = this.datepipe.transform(this.gapLogicSWService.getNonLeapDate(), Constants.SERVICE_DATE_FORMAT)!;
              appDate.beginWindowJulian = Utils.getDayOfYear(this.gapLogicSWService.getNonLeapDate());
              appDate.endWindow = this.datepipe.transform(new Date(Utils.GetEndDateByApplication(appDate.beginWindow!, node.data?.applicationInterval, node.data?.numberOfApplications)), Constants.SERVICE_DATE_FORMAT)!;
              appDate.endWindowJulian = Utils.getDayOfYear(new Date(Utils.GetEndDateByApplication(appDate.beginWindow!, node.data?.applicationInterval, node.data?.numberOfApplications)));
              appDate.emergencyDate = this.datepipe.transform(new Date(1999, 0, 1), Constants.SERVICE_DATE_FORMAT)!;
              appDate.harvestDate = this.datepipe.transform(new Date(1999, 0, 31), Constants.SERVICE_DATE_FORMAT)!;
              appDates.push({ data: appDate });
            });
          }
          else {
            this.applicationDatesByCrop = this.applicationDates.filter((x: ApplicationDateSurfaceWater) => x.beginWindow != null || x.endWindow != null);
            this.applicationDatesByCrop?.forEach((appDate: ApplicationDateSurfaceWater) => {
              if (Utils.getFirstMatchBetweenTwoArrays(this.selectedModels, [...Constants.SWASH_VERSIONS, Constants.MODELS.SWAN])) {
                const currentYear = new Date(2001, 0, 1).getFullYear();
                appDate.endWindow = Utils.GetEndDateByApplication(appDate.beginWindow!, node.data?.applicationInterval, node.data?.numberOfApplications);
                if (!appDate.appDateVersion) {
                  appDate.beginWindow = appDate?.beginWindow!.replace(/(\d{4})/, currentYear.toString());
                  if (appDate?.endWindow)
                    appDate.endWindow = appDate?.endWindow!.replace(/(\d{4})/, currentYear.toString());
                }
              }
              appDates.push({ data: appDate });
            });
          }

          if (appDates.length > 0) {
            this.onSaveApplicationDates({ selectedRows: appDates, parentRowNode: node });
          }
        });
    }
  }

  private loadSwash63ApplicationDates(node: RowNode, resetStart = false, resetEnd = false, showDialog = false): void {
    const row = node.data;
    const cropPk = row.cropAppDatePk ?? this.getCropValueMatchings(node);
    const cropName = this.cropList?.find((crop: CropList) => crop.CropListPk === cropPk)?.CropName;
    const request = {};
    this.swash63DateRequests.set(row, request);
    this.swash63GapLogicService.setRowWithoutBbch(row, false);
    this.swash63GapLogicService.clearValidationWarnings(row);
    row.allocatedBbchStart = null;
    row.allocatedBbchEnd = null;
    this.grid.gridApi.refreshCells({ rowNodes: [node], force: true });
    if (!cropPk) {
      if (resetStart) {
        row.applicationDate = null;
        row.applicationSchemeXApplicationWindow = [];
      }
      this.grid.gridApi.refreshCells({ rowNodes: [node], force: true });
      return;
    }
    const existing: ApplicationSchemeXApplicationWindow[] = row.applicationSchemeXApplicationWindow ?? [];
    const ranges = existing.map(window => ({
      applicationDateSurfaceWaterPk: window.applicationDateSurfaceWaterPk,
      location: window.location,
      season: window.season,
      bbchStart: resetStart ? undefined : window.bbchStart,
      bbchEnd: resetEnd ? undefined : window.bbchEnd,
    }));
    forkJoin({
      dates: this.swash63GapLogicService.getApplicationDates(
        cropPk, row.bbchEarliest ?? '', row.bbchLatest, ranges, cropName,
      ),
      allocated: this.swash63GapLogicService.getAllocatedBbch(cropPk, row.bbchEarliest, row.bbchLatest, cropName),
      hasBbchData: this.swash63GapLogicService.getCropHasBbchData(cropPk, cropName),
    }).subscribe({
      next: ({ dates, allocated, hasBbchData }) => {
        if (this.swash63DateRequests.get(row) !== request || this.grid.gridApi.getRowNode(node.id)?.data !== row) return;
        const oldValue = JSON.stringify([
          row.bbchEarliest,
          row.bbchLatest,
          row.applicationDate,
          row.applicationSchemeXApplicationWindow,
        ]);
        if (!hasBbchData) {
          row.bbchEarliest = null;
          row.bbchLatest = null;
        }
        row.allocatedBbchStart = allocated.allocatedBbchStart;
        row.allocatedBbchEnd = allocated.allocatedBbchEnd;
        const previousFor = (date: Swash63ApplicationWindow) =>
          date.rangeIndex != null && date.rangeIndex >= 0 ? existing[date.rangeIndex] : undefined;
        const windows = dates.map(date => {
          const previous = previousFor(date);
          return {
            ...date,
            beginWindow: date.noBbch || resetStart ? date.beginWindow :
              this.datepipe.transform(previous?.firstDate ?? row.applicationDate ?? date.beginWindow, Constants.SERVICE_DATE_FORMAT) ?? undefined,
            endWindow: date.noBbch || resetEnd ? date.endWindow :
              this.datepipe.transform(previous?.endDate ?? date.endWindow, Constants.SERVICE_DATE_FORMAT) ?? undefined,
            runOff: previous?.runOff,
            sprayDrift: previous?.sprayDrift,
            deposition: previous?.deposition,
            applicationSchemeXApplicationWindowPk: previous?.applicationSchemeXApplicationWindowPk,
          };
        });
        const rowWithoutBbch = !hasBbchData || (windows.length > 0 && windows.every(window => window.noBbch));
        this.swash63GapLogicService.setRowWithoutBbch(row, rowWithoutBbch);
        if (showDialog) {
          this.swash63GapLogicService.updateApplicationWarnings(row, windows);
          if (!dates.length && !Utils.isEmptyValue(row.bbchEarliest)) this.setNoSwash63ScenariosWarning(row);
          this.grid.gridApi.refreshCells({ rowNodes: [node], force: true });
          if (!dates.length) return;
          windows.forEach(window => this.swash63GapLogicService.updateScenarioWarnings(window));
          this.applicationDatesByCrop = windows;
          this.applicationDialog.showDialog(node, this.compartment, cropPk);
          return;
        }
        const selected = windows.filter(window => existing.length === 0 || !!previousFor(window));
        row.applicationSchemeXApplicationWindow = selected.map(window => ({
          ...previousFor(window),
          applicationDateSurfaceWaterPk: window.applicationDateSurfaceWaterPk,
          location: window.location,
          season: window.season,
          firstDate: window.beginWindow,
          endDate: window.endWindow,
          bbchStart: window.bbchStart,
          bbchEnd: window.bbchEnd,
        }));
        row.applicationDate = this.swash63GapLogicService.getCommonApplicationDate(row.applicationSchemeXApplicationWindow);
        if (this.isProjectOwnershipValid && oldValue !== JSON.stringify([
          row.bbchEarliest,
          row.bbchLatest,
          row.applicationDate,
          row.applicationSchemeXApplicationWindow,
        ])) {
          this.grid.CreateTransaction(node.id, node.id, null, row);
        }
        this.swash63GapLogicService.updateApplicationWarnings(row, selected);
        if (!dates.length && !Utils.isEmptyValue(row.bbchEarliest)) this.setNoSwash63ScenariosWarning(row);
        this.grid.gridApi.refreshCells({ rowNodes: [node], force: true });
      },
      error: () => {
        if (this.swash63DateRequests.get(row) === request) {
          void Swal.fire({ icon: 'error', text: 'Unable to load BBCH dates from AppDate 3.08. Please try again.' });
        }
      },
    });
  }

  override onBlurInputText(event: { row: any; field?: string; id?: string }): void {
    super.onBlurInputText(event);
    if (!this.hasSwash63) return;
    if (event.row?.isChild && event.field === 'min_application_interval') {
      const parentNode = this.grid.gridApi.getRowNode(event.row.parentRowNodeId);
      if (parentNode) {
        this.updateSwash63ApplicationIntervals(parentNode.data);
        this.grid.CreateTransaction(parentNode.id, parentNode.id, null, parentNode.data);
        this.grid.detailGridApi?.refreshCells({ force: true });
      }
    }
    if (event.field === Constants.GAP_FIELD_NAMES.SWASH_CROP_INTERCEPTION) {
      this.refreshSwash63InterceptionCell(event.id);
    }
    if (event.row?.isChild && event.field === 'crop_interception') {
      this.grid.detailGridApi?.refreshCells({ force: true });
    }
  }

  private refreshSwash63InterceptionCell(nodeId?: string): void {
    const node = nodeId != null ? this.grid?.gridApi?.getRowNode(nodeId) : undefined;
    if (!node) return;
    this.grid.gridApi.refreshCells({ rowNodes: [node], columns: [Constants.GAP_FIELD_NAMES.SWASH_CROP_INTERCEPTION], force: true });
  }

  private updateSwash63InterceptionCrop(row: any): void {
    const matching = row.cropAppDatePk == null ? undefined : this.cropListMatchings.find(candidate =>
      candidate.compartment === this.compartment &&
      candidate.geography === this.selectedProject?.geography &&
      candidate.appDateCrops?.includes(row.cropAppDatePk),
    );
    const interceptionCropPk = matching?.driftInterceptionCrops?.find(cropPk => cropPk != null) ?? null;
    row.cropDriftInterceptionPk = interceptionCropPk;
    row.cropInterception = null;
    row.cropCoverage = null;
    if (!row.useSwashCropInterception) {
      row.swashCropInterception = null;
    }
    if (interceptionCropPk == null) {
      this.updateSwash63ChildInterceptions(row);
      return;
    }
    this.gapLogicSWService.setCropInterceptionByCropAndBBCH(row, this.compartment);
    this.updateSwash63ChildInterceptions(row);
  }

  private updateSwash63ApplicationIntervals(row: any): void {
    const children: any[] = row.children?.length ? row.children :
      row.applicationSchemeXApplication?.filter((application: any) => application.application_number != null) ??
      [];
    const direction = row.appTimingDirection ?? Constants.APP_TIMING_DIRECTIONS.FORWARD;
    row.appTimingDirection = direction;

    children.forEach(child => {
      const applicationNumber = Number(child.application_number);
      const isFixedInterval =
        (direction === Constants.APP_TIMING_DIRECTIONS.FORWARD && applicationNumber === 1) ||
        (direction === Constants.APP_TIMING_DIRECTIONS.BACKWARD && applicationNumber === Number(row.numberOfApplications));
      if (isFixedInterval) {
        if (child.min_application_interval != null && Number(child.min_application_interval) !== 0) {
          child.swash63PreviousApplicationInterval = child.min_application_interval;
        }
        child.min_application_interval = 0;
        return;
      }
      if (child.swash63PreviousApplicationInterval != null) {
        child.min_application_interval = child.swash63PreviousApplicationInterval;
        delete child.swash63PreviousApplicationInterval;
      }
      if (child.min_application_interval == null || child.min_application_interval === '') return;

      const interval = Number(child.min_application_interval);
      if (Number.isFinite(interval)) {
        child.min_application_interval = interval === 0
          ? 0
          : direction === Constants.APP_TIMING_DIRECTIONS.BACKWARD
            ? -Math.abs(interval)
            : Math.abs(interval);
      }
    });
  }

  private updateSwash63ChildInterceptions(row: any, resetToDefault = false): void {
    const children: any[] = row.children?.length ? row.children :
      row.applicationSchemeXApplication?.filter((application: any) => application.application_number != null) ??
      [];
    const automaticValue = row.cropInterception ?? null;

    children.forEach(child => {
      const currentValue = child.crop_interception;
      const previousAutomaticValue = child.defaultCropInterception;
      if (resetToDefault || currentValue == null || currentValue === '' ||
        this.isSameInterceptionValue(currentValue, previousAutomaticValue)) {
        child.crop_interception = automaticValue;
      }
      child.defaultCropInterception = automaticValue;
    });
  }

  private isSameInterceptionValue(value: any, expected: any): boolean {
    if (value == null || expected == null || value === '' || expected === '') return value === expected;
    const numericValue = Number(value);
    const numericExpected = Number(expected);
    return Number.isFinite(numericValue) && Number.isFinite(numericExpected) &&
      Math.abs(numericValue - numericExpected) <= 1e-6;
  }

  private setNoSwash63ScenariosWarning(row: object): void {
    this.swash63GapLogicService.setFieldWarnings(row, 'bbchEarliest', [
      'No application scenarios are available for the selected AppDate crop.',
    ]);
  }

  private getSurfaceWaterApplicationDates(cropListPk: number, bbch: number): Promise<ApplicationDateSurfaceWater[]> {
    return this.useSwash63Dates
      ? firstValueFrom(this.swash63GapLogicService.getApplicationDates(cropListPk, String(bbch)))
      : this.gapLogicSWService.getApplicationDatesSurfaceWaterByCropListPkAndBBCH(cropListPk, bbch);
  }
}
